import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { loadState, type ManualFlag, type ReportState, type ReviewEntry } from "./reportStore";

// Server-synced report review state (dismissals, edited comments/titles, meaning
// ratings, category overrides, manual flags, dismiss-all signatures, Find mode's
// heard ticks). "Picked" flags already persist via the Pickup table; this
// carries everything else off the browser's localStorage so a project's review
// follows the reviewer across devices.
//
// Several people (or tabs) can work on one book at once, so a save is what this
// report CHANGED since it last heard from the server (diffReviewState), and the
// server applies it to the state as it is now (server: review_merge.py) and
// answers with the merged state, which the report takes on - keeping any edits
// made while the save was out (applyReviewChanges). Saving the whole state, the
// way it was done before, let the last save erase everyone else's work.

const REVIEW_URL = (projectId: string) => `/api/modules/proofing/projects/${projectId}/review`;

function asState(d: Partial<ReportState> | null | undefined): ReportState {
  return {
    marks: d?.marks || {},
    manual: d?.manual || {},
    dismissed_signatures: d?.dismissed_signatures || [],
    heard: d?.heard || {},
  };
}

export async function fetchReviewState(projectId: string): Promise<ReportState> {
  const r = await api<{ data: Partial<ReportState> | null }>(REVIEW_URL(projectId));
  return asState(r.data);
}

/** The server's state, or `null` when it hasn't changed since `since` (its
 *  `updatedAt` as last seen) - the cheap check a report makes every so often. */
export async function fetchReviewUpdate(
  projectId: string, since?: string | null,
): Promise<{ state: ReportState | null; updatedAt: string | null }> {
  const q = since ? `?since=${encodeURIComponent(since)}` : "";
  const r = await api<{ data?: Partial<ReportState> | null; unchanged?: boolean; updated_at: string | null }>(
    REVIEW_URL(projectId) + q);
  return { state: r.unchanged ? null : asState(r.data), updatedAt: r.updated_at ?? null };
}

/** Save what changed; answers with everyone's merged state. */
export async function patchReviewState(
  projectId: string, changes: ReviewChanges,
): Promise<{ state: ReportState; updatedAt: string | null }> {
  const r = await api<{ data: Partial<ReportState>; updated_at: string | null }>(REVIEW_URL(projectId), {
    method: "PATCH", body: JSON.stringify({ changes }),
  });
  return { state: asState(r.data), updatedAt: r.updated_at ?? null };
}

// ---- What changed ------------------------------------------------------------

/** One save: per flag, the fields that changed (null = cleared; a whole flag
 *  null = forgotten); manual flags by id (null = removed); "dismiss all
 *  identical" switches; words newly heard. The same shape review_merge applies. */
export interface ReviewChanges {
  marks?: Record<string, Record<string, unknown> | null>;
  manual?: Record<string, Record<string, ManualFlag | null>>;
  signatures?: Record<string, boolean>;
  heard?: Record<string, number[]>;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
// A mark whose fields were all cleared (set to undefined) is no mark.
const live = (e: ReviewEntry | undefined) =>
  !!e && Object.values(e).some((v) => v !== undefined);

/** What `next` changed from `base` - null when nothing did. */
export function diffReviewState(base: ReportState, next: ReportState): ReviewChanges | null {
  const out: ReviewChanges = {};

  const marks: NonNullable<ReviewChanges["marks"]> = {};
  const bm = base.marks || {}, nm = next.marks || {};
  for (const key of new Set([...Object.keys(bm), ...Object.keys(nm)])) {
    const b = bm[key], n = nm[key];
    if (!live(n)) { if (live(b)) marks[key] = null; continue; }
    const patch: Record<string, unknown> = {};
    const bf = (b || {}) as Record<string, unknown>, nf = n as Record<string, unknown>;
    for (const f of new Set([...Object.keys(bf), ...Object.keys(nf)])) {
      if (nf[f] === undefined) { if (bf[f] !== undefined) patch[f] = null; }
      else if (!same(bf[f], nf[f])) patch[f] = nf[f];
    }
    if (Object.keys(patch).length) marks[key] = patch;
  }
  if (Object.keys(marks).length) out.marks = marks;

  const manual: NonNullable<ReviewChanges["manual"]> = {};
  const bmf = base.manual || {}, nmf = next.manual || {};
  for (const cid of new Set([...Object.keys(bmf), ...Object.keys(nmf)])) {
    const was = new Map((bmf[cid] || []).map((f) => [f.id, f]));
    const now = new Map((nmf[cid] || []).map((f) => [f.id, f]));
    const ch: Record<string, ManualFlag | null> = {};
    for (const [id, f] of now) if (!same(was.get(id), f)) ch[id] = f;
    for (const id of was.keys()) if (!now.has(id)) ch[id] = null;
    if (Object.keys(ch).length) manual[cid] = ch;
  }
  if (Object.keys(manual).length) out.manual = manual;

  const sigs: Record<string, boolean> = {};
  const bs = new Set(base.dismissed_signatures || []), ns = new Set(next.dismissed_signatures || []);
  for (const sg of ns) if (!bs.has(sg)) sigs[sg] = true;
  for (const sg of bs) if (!ns.has(sg)) sigs[sg] = false;
  if (Object.keys(sigs).length) out.signatures = sigs;

  const heard: Record<string, number[]> = {};
  for (const [cid, words] of Object.entries(next.heard || {})) {
    const had = new Set(base.heard?.[cid] || []);
    const add = words.filter((w) => !had.has(w));
    if (add.length) heard[cid] = add;
  }
  if (Object.keys(heard).length) out.heard = heard;

  return Object.keys(out).length ? out : null;
}

/** `state` with `ch` applied, exactly as the server applies a save. */
export function applyReviewChanges(state: ReportState, ch: ReviewChanges): ReportState {
  const next: ReportState = {
    marks: { ...state.marks },
    manual: { ...state.manual },
    dismissed_signatures: [...(state.dismissed_signatures || [])],
    heard: { ...(state.heard || {}) },
  };
  for (const [key, patch] of Object.entries(ch.marks || {})) {
    if (patch === null) { delete next.marks[key]; continue; }
    const e: Record<string, unknown> = { ...(next.marks[key] || {}) };
    for (const [f, v] of Object.entries(patch)) { if (v === null) delete e[f]; else e[f] = v; }
    if (live(e as ReviewEntry)) next.marks[key] = e as ReviewEntry;
    else delete next.marks[key];
  }
  for (const [cid, flags] of Object.entries(ch.manual || {})) {
    const have = [...(next.manual[cid] || [])];
    for (const [id, f] of Object.entries(flags)) {
      const at = have.findIndex((x) => x.id === id);
      if (f === null) { if (at >= 0) have.splice(at, 1); }
      else if (at >= 0) have[at] = f;
      else have.push(f);
    }
    if (have.length) next.manual[cid] = have;
    else delete next.manual[cid];
  }
  for (const [sg, on] of Object.entries(ch.signatures || {})) {
    const i = next.dismissed_signatures.indexOf(sg);
    if (on && i < 0) next.dismissed_signatures.push(sg);
    if (!on && i >= 0) next.dismissed_signatures.splice(i, 1);
  }
  for (const [cid, words] of Object.entries(ch.heard || {})) {
    next.heard![cid] = Array.from(new Set([...(next.heard![cid] || []), ...words])).sort((a, b) => a - b);
  }
  return next;
}

export const sameReviewState = (a: ReportState, b: ReportState) => same(a, b);

// The server's state as this browser last heard it, beside the local copy, so
// edits made here but never saved (offline, a closed tab) are told apart from
// edits made elsewhere when the report opens again.
const BASE_KEY = (projectId: string) => `proofbox.report.${projectId}.base`;
export function loadBase(projectId: string): ReportState | null {
  try {
    const raw = localStorage.getItem(BASE_KEY(projectId));
    return raw ? asState(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}
export function saveBase(projectId: string, state: ReportState): void {
  try { localStorage.setItem(BASE_KEY(projectId), JSON.stringify(state)); } catch { /* private window */ }
}

/** Union of two review states. The server is the source of truth for cross-device
 * sync, so it wins on per-flag mark conflicts; manual flags merge by id and
 * dismissed signatures union. Local-only entries not yet pushed still survive. */
export function mergeReviewState(local: ReportState, server: ReportState): ReportState {
  const manual: Record<string, ManualFlag[]> = {};
  const chapters = new Set([...Object.keys(local.manual || {}), ...Object.keys(server.manual || {})]);
  for (const cid of chapters) {
    const byId = new Map<string, ManualFlag>();
    for (const m of local.manual[cid] || []) byId.set(m.id, m);
    for (const m of server.manual[cid] || []) byId.set(m.id, m); // server wins on an id clash
    manual[cid] = Array.from(byId.values());
  }
  return {
    marks: { ...(local.marks || {}), ...(server.marks || {}) }, // server wins per key
    manual,
    dismissed_signatures: Array.from(
      new Set([...(local.dismissed_signatures || []), ...(server.dismissed_signatures || [])]),
    ),
    heard: mergeHeard(local.heard, server.heard),
  };
}

/** Heard ticks only ever add up: a word heard on either device was heard. */
function mergeHeard(a: Record<string, number[]> = {}, b: Record<string, number[]> = {}): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  for (const cid of new Set([...Object.keys(a), ...Object.keys(b)])) {
    out[cid] = Array.from(new Set([...(a[cid] || []), ...(b[cid] || [])])).sort((x, y) => x - y);
  }
  return out;
}

/** Read-only merged review state for non-editing consumers (the project page's
 * review tallies): server ∪ localStorage, so the per-take ★/✕/• counts reflect
 * work done on any device. */
export function useReviewState(projectId: string | undefined) {
  return useQuery({
    queryKey: ["review", projectId],
    // Display only: the report's own copy (and what it has heard from the
    // server) is left alone, so its next save still knows what it changed.
    queryFn: async () => mergeReviewState(loadState(projectId!), await fetchReviewState(projectId!)),
    enabled: !!projectId,
    staleTime: 10_000,
  });
}
